import { createWebHistory, createRouter } from "vue-router";
import axios from 'axios';
import routes from './routes';
import appConfig from "../../app.config";

// Strip a single trailing slash so `/foo/` and `/foo` compare equal.
const normalizePath = (p) => {
  if (!p || p === '/') return p;
  return p.endsWith('/') ? p.slice(0, -1) : p;
};

// Look up a route's `meta.permission` (if any) by its path. We need the path-based
// lookup because the menu API only gives us URLs, not route names.
const requiredPermissionForPath = (path) => {
  if (!path) return null;
  const target = normalizePath(path);
  const match = routes.find((r) => normalizePath(r.path) === target);
  return match?.meta?.permission || null;
};

// Walk a permission-filtered menu tree and return the first navigable route the
// user can actually load. Skips `/` (would loop back to the gate) and any route
// whose meta.permission the user lacks.
const findFirstMenuRoute = (menus, perms) => {
  const permsSet = new Set(perms || []);
  const canEnter = (path) => {
    const required = requiredPermissionForPath(path);
    return !required || permsSet.has(required);
  };

  for (const menu of menus || []) {
    if (menu.route && menu.route !== '/' && canEnter(menu.route)) {
      return menu.route;
    }
    const childRoute = findFirstMenuRoute(menu.children || [], perms);
    if (childRoute) return childRoute;
  }
  return null;
};

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// API based Authentication using local Laravel API
// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  // Check for token passed from portal via URL query parameter
  const urlCode = routeTo.query.code;

  if (urlCode) {
    try {
      const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
      const response = await axios.post(`${apiUrl}/api/portal-auth`, { code: urlCode });

      const { token, user } = response.data;

      localStorage.setItem('auth_token', token);
      sessionStorage.setItem('authUser', JSON.stringify(user));
      localStorage.setItem('user', JSON.stringify(user));

      // Redirect to dashboard without the token in the URL
      return next({ name: 'default', replace: true });
    } catch (error) {
      console.error('Portal authentication failed:', error);
      return next({ name: 'login', replace: true });
    }
  }

  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

  // If auth isn't required, continue
  if (!authRequired) return next();



  // Check for token in localStorage
  const token = localStorage.getItem('auth_token');

  const user = sessionStorage.getItem('authUser');

  // If no token, redirect to login
  if (!token) {
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }

  // Check lock screen state
  const isLocked = sessionStorage.getItem('screen_locked') === 'true';

  if (isLocked && routeTo.name !== 'lock-screen' && routeTo.name !== 'login') {
    // User is locked, redirect to lock screen
    return next({ name: 'lock-screen' });
  }

  if (!isLocked && routeTo.name === 'lock-screen') {
    // Not locked but trying to access lock screen, go home
    return next({ name: 'default' });
  }

  // Resolve a usable permissions array, refetching /api/user if the cached
  // sessionStorage payload is missing the `permissions` field (older sessions).
  // Returns { perms, isSuperAdmin } or null if unreachable.
  const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
  // Super-admin is the `super-admin` Spatie role on the backend. The user payload
  // exposes it both as `is_super_admin` (new field) and as `role.name === 'super-admin'`.
  // Accept either so cached sessions from before the field existed still work.
  const isSuperAdminUser = (u) =>
    !!u?.is_super_admin || u?.role?.name === 'super-admin';

  const resolvePerms = async (authUser) => {
    if (!authUser) return null;
    if (isSuperAdminUser(authUser)) return { perms: [], isSuperAdmin: true };
    if (Array.isArray(authUser.permissions)) {
      return { perms: authUser.permissions, isSuperAdmin: false };
    }
    try {
      const u = await axios.get(`${apiUrl}/api/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      sessionStorage.setItem('authUser', JSON.stringify(u.data));
      localStorage.setItem('user', JSON.stringify(u.data));
      if (isSuperAdminUser(u.data)) return { perms: [], isSuperAdmin: true };
      return {
        perms: Array.isArray(u.data?.permissions) ? u.data.permissions : [],
        isSuperAdmin: false,
      };
    } catch (e) {
      return null;
    }
  };

  // Pick the first menu route the user is allowed to actually load.
  const firstAllowedRoute = async (perms) => {
    try {
      const r = await axios.get(`${apiUrl}/api/menus`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const menus = Array.isArray(r.data) ? r.data : [];
      return findFirstMenuRoute(menus, perms);
    } catch (e) { return null; }
  };

  // Route permission gate — every authenticated route may declare `meta.permission`.
  // - Dashboard `/` is special: silently falls back to the first allowed menu
  //   (so users land somewhere usable instead of an error page on first login).
  // - Every other forbidden route shows the Access Denied page so the user sees
  //   *why* they can't proceed instead of being silently redirected.
  const applyRoutePermissionGate = async (authUser) => {
    // Never gate the access-denied page itself — that would loop.
    if (routeTo.name === 'access-denied') return null;

    const required = routeTo.matched
      .map((r) => r.meta?.permission)
      .filter(Boolean);
    if (required.length === 0) return null;

    const resolved = await resolvePerms(authUser);
    if (!resolved || resolved.isSuperAdmin) return null;

    const hasAll = required.every((p) => resolved.perms.includes(p));
    if (hasAll) return null;

    // Dashboard landing — try to silently send the user to their first usable menu.
    if (routeTo.name === 'default') {
      const target = await firstAllowedRoute(resolved.perms);
      if (target && target !== '/') return { path: target };
    }

    return {
      name: 'access-denied',
      query: {
        from: routeTo.fullPath,
        permission: required.join(','),
      },
    };
  };

  // Vehicle Availability Gate — mirrors the lock-screen pattern above.
  // Returns a route object to redirect to, or null to proceed.
  const applyAvailabilityGate = async (authUser) => {
    if (!authUser || isSuperAdminUser(authUser)) return null;

    let gateRoleIds = [];
    const cached = sessionStorage.getItem('availability_gate_roles');
    if (cached) {
      try { gateRoleIds = JSON.parse(cached); } catch { gateRoleIds = []; }
    } else {
      try {
        const s = await axios.get(`${apiUrl}/api/system-settings`);
        const raw = s.data?.['availability_gate_role_ids'];
        gateRoleIds = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(gateRoleIds)) gateRoleIds = [];
        sessionStorage.setItem('availability_gate_roles', JSON.stringify(gateRoleIds));
      } catch (e) { gateRoleIds = []; }
    }

    const gateRoleSet = new Set(gateRoleIds.map(Number));
    const userRoleId = authUser.role?.id != null ? Number(authUser.role.id) : null;
    const roleApplies = userRoleId !== null && gateRoleSet.has(userRoleId);

    if (roleApplies && routeTo.name !== 'vehicle-availability-gate' && routeTo.name !== 'login') {
      try {
        const r = await axios.get(`${apiUrl}/api/vehicles/unassigned-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if ((r.data?.count ?? 0) > 0) {
          return { name: 'vehicle-availability-gate' };
        }
      } catch (e) { /* fail-open */ }
    }

    if (!roleApplies && routeTo.name === 'vehicle-availability-gate') {
      return { name: 'default' };
    }
    return null;
  };

  // If we have a token and user, run gate check then continue
  if (token && user) {
    let authUser = null;
    try { authUser = JSON.parse(user); } catch { /* ignore */ }
    const gateRedirect = await applyAvailabilityGate(authUser);
    if (gateRedirect) return next(gateRedirect);
    const permissionRedirect = await applyRoutePermissionGate(authUser);
    if (permissionRedirect) return next(permissionRedirect);
    return next();
  }

  // If we have token but no user, try to fetch user from API
  try {
    const response = await axios.get(`${apiUrl}/api/user`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    sessionStorage.setItem('authUser', JSON.stringify(response.data));
    localStorage.setItem('user', JSON.stringify(response.data));
    const gateRedirect = await applyAvailabilityGate(response.data);
    if (gateRedirect) return next(gateRedirect);
    const permissionRedirect = await applyRoutePermissionGate(response.data);
    if (permissionRedirect) return next(permissionRedirect);
    next();
  } catch (error) {
    // Token invalid, clear and redirect to login
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('authUser');
    next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }
});



router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
